import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import ProductImageGallery from '@/components/admin/ProductImageGallery';
import { toast } from 'sonner';
import { ArrowRight, Save, Plus, Trash2, ExternalLink } from 'lucide-react';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import SupplierQuickCreate from '@/components/admin/SupplierQuickCreate';
import { SimpleGroupBuyFields } from '@/components/admin/SimpleGroupBuyFields';
import { LiquidationSaleFields } from '@/components/admin/LiquidationSaleFields';
import { ProductAI } from '@/components/admin/ProductAI';
import { ProductOrders } from '@/components/admin/ProductOrders';
import { useAdminStats } from '@/hooks/useAdminStats';

type PriceTier = {
  participants_required: number;
  discount_percent: number;
  price_per_unit: number;
};

type Product = {
  id: string;
  name: string;
  description: string;
  sku: string | null;
  slug: string;
  category_id: string;
  subcategory_id: string;
  measurement_unit: 'square_meter' | 'kilogram' | 'liter' | 'meter' | 'unit' | 'surface' | 'cubic_meter' | 'package_by_meter' | 'package_by_square_meter';
  total_inventory: number;
  original_inventory: number | null;
  min_quantity: number;
  max_quantity: number | null;
  step_quantity: number;
  price_per_unit: number;
  cost_price: number | null;
  original_price: number | null;
  package_coverage_per_unit: number | null;
  city: string;
  sale_type: 'immediate' | 'timer' | 'liquidation';
  sale_start: string | null;
  sale_end: string | null;
  delivery_option: 'pickup_only' | 'delivery_only' | 'both';
  supplier_name: string;
  supplier_id: string | null;
  images: string[];
  is_active: boolean;
  grade: 'A' | 'B' | 'C' | 'D';
  group_buy_enabled: boolean;
  group_buy_target_quantity: number | null;
  group_buy_current_quantity: number | null;
  group_buy_locked_price: number | null;
  group_buy_end_date: string | null;
  group_buy_limit_to_target?: boolean;
  group_buy_delivery_days?: number | null;
  // Liquidation fields
  liquidation_sale_enabled: boolean;
  liquidation_total_inventory: number | null;
  liquidation_current_inventory: number | null;
  liquidation_threshold_percent: number | null;
  liquidation_end_date: string | null;
  liquidation_delivery_days: number | null;
  show_inventory_quantity: boolean;
  // Dimension fields
  dimension_width: number | null;
  dimension_height: number | null;
  dimension_depth: number | null;
  dimension_unit: string | null;
  dimension_width_unit: string | null;
  dimension_height_unit: string | null;
  dimension_depth_unit: string | null;
  // Additional info
  country_of_origin: string | null;
  material: string | null;
  finish: string | null;
  supplier_sku: string | null;
  notes: string | null;
  simulation_images: string[];
};

type Category = {
  id: string;
  name: string;
};

type Subcategory = {
  id: string;
  name: string;
  category_id: string;
};

type Supplier = {
  id: string;
  name: string;
  city: string | null;
};

export default function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadProductData();
    }
    loadCategories();
    loadSuppliers();
  }, [id]);

  useEffect(() => {
    if (product?.category_id) {
      loadSubcategories(product.category_id);
    }
  }, [product?.category_id]);

  const loadProductData = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading product:', error);
        toast.error('שגיאה בטעינת המוצר');
        navigate('/admin');
        return;
      }

      // Ensure new fields have default values
      const productData = {
        ...data,
        slug: data.slug || data.name.toLowerCase().replace(/\s+/g, '-'),
        supplier_id: (data as any).supplier_id || null,
        package_coverage_per_unit: (data as any).package_coverage_per_unit ?? null,
        group_buy_enabled: (data as any).group_buy_enabled ?? false,
        group_buy_target_quantity: (data as any).group_buy_target_quantity ?? null,
        group_buy_current_quantity: (data as any).group_buy_current_quantity ?? null,
        group_buy_locked_price: (data as any).group_buy_locked_price ?? null,
        group_buy_end_date: (data as any).group_buy_end_date ?? null,
        group_buy_limit_to_target: (data as any).group_buy_limit_to_target ?? false,
        supplier_sku: (data as any).supplier_sku ?? null,
        notes: (data as any).notes ?? null,
        simulation_images: (data as any).simulation_images ?? [],
      } as Product;

      setProduct(productData);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('שגיאה בטעינת המוצר');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error loading categories:', error);
        return;
      }

      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('name');

      if (error) {
        console.error('Error loading subcategories:', error);
        return;
      }

      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, city')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error loading suppliers:', error);
        return;
      }

      setSuppliers(data || []);

      // Set default supplier to "אקסס מרקט" if no supplier is selected
      if (product && !product.supplier_id && data && data.length > 0) {
        const defaultSupplier = data.find(s => s.name.includes('אקסס מרקט') || s.name.includes('אקסס'));
        if (defaultSupplier) {
          updateProduct('supplier_id', defaultSupplier.id);
        }
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    // Validate slug
    if (!product.slug) {
      toast.error('יש להזין URL למוצר');
      return;
    }

    const isSlugValid = await validateSlug(product.slug, product.id);
    if (!isSlugValid) {
      toast.error('URL זה כבר קיים. אנא בחר URL אחר');
      return;
    }

    // Validation for original price
    if (product.original_price && product.original_price <= product.price_per_unit) {
      toast.error('המחיר המקורי חייב להיות גבוה מהמחיר הנוכחי (אחרי הנחה)');
      return;
    }

    // Validation for min/max quantity
    if (product.max_quantity && product.max_quantity < product.min_quantity) {
      toast.error(`הכמות המקסימלית (${product.max_quantity}) לא יכולה להיות נמוכה מהכמות המינימלית (${product.min_quantity})`);
      return;
    }

    if (product.min_quantity && product.max_quantity && product.min_quantity > product.max_quantity) {
      toast.error(`הכמות המינימלית (${product.min_quantity}) לא יכולה להיות גבוהה מהכמות המקסימלית (${product.max_quantity})`);
      return;
    }

    setSaving(true);
    try {
      console.log('Saving product with images:', product.images);
      console.log('Saving product with images:', product.images);

      // Ensure we have clean arrays and proper values
      // Clean and prepare images array - filter out any null, undefined, or empty strings
      const cleanImages = Array.isArray(product.images)
        ? product.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
        : [];

      console.log('Saving product with images:', cleanImages);
      console.log('Original product images:', product.images);

      const updateData = {
        name: product.name,
        description: product.description,
        sku: product.sku || null,
        slug: product.slug,
        category_id: product.category_id,
        subcategory_id: product.subcategory_id,
        measurement_unit: product.measurement_unit,
        total_inventory: product.total_inventory,
        original_inventory: product.original_inventory,
        min_quantity: product.min_quantity,
        max_quantity: product.max_quantity,
        step_quantity: product.step_quantity,
        price_per_unit: product.price_per_unit,
        cost_price: product.cost_price,
        original_price: product.original_price,
        package_coverage_per_unit: product.package_coverage_per_unit,
        city: (() => {
          const selectedSupplier = suppliers.find(s => s.id === product.supplier_id);
          return selectedSupplier?.city || product.city || 'לא צוין';
        })(),
        sale_type: product.sale_type,
        sale_start: product.sale_start,
        sale_end: product.sale_end,
        delivery_option: product.delivery_option,
        supplier_id: (product as any).supplier_id || null,
        supplier_name: product.supplier_name,
        images: cleanImages,
        is_active: product.is_active,
        grade: product.grade || 'B',
        group_buy_enabled: product.group_buy_enabled,
        group_buy_target_quantity: product.group_buy_target_quantity,
        group_buy_locked_price: product.group_buy_locked_price,
        group_buy_end_date: product.group_buy_end_date,
        group_buy_limit_to_target: product.group_buy_limit_to_target,
        // Liquidation fields
        liquidation_sale_enabled: product.liquidation_sale_enabled,
        // Fix: Map original_inventory to liquidation_total (the goal)
        // and total_inventory to liquidation_current (what's left)
        liquidation_total_inventory: product.liquidation_sale_enabled ? (product.original_inventory || product.total_inventory) : null,
        liquidation_current_inventory: product.liquidation_sale_enabled ? product.total_inventory : null,
        liquidation_threshold_percent: product.liquidation_threshold_percent,
        liquidation_delivery_days: product.liquidation_delivery_days,
        simulation_images: product.simulation_images,
        // Description validation removal
        // Dimension fields
        dimension_width: product.dimension_width,
        dimension_height: product.dimension_height,
        dimension_depth: product.dimension_depth,
        dimension_unit: product.dimension_unit,
        dimension_width_unit: product.dimension_width_unit,
        dimension_height_unit: product.dimension_height_unit,
        dimension_depth_unit: product.dimension_depth_unit,
        // Additional info
        country_of_origin: product.country_of_origin,
        material: product.material,

        finish: product.finish,
        supplier_sku: product.supplier_sku,
        notes: product.notes,
      };

      console.log('Clean update data:', updateData);

      const { data, error } = await supabase
        .from('products')
        .update(updateData as any)
        .eq('id', id)
        .select();

      console.log('Update result:', { data, error });

      if (error) {
        console.error('Error updating product:', error);
        toast.error('שגיאה בעדכון המוצר');
        return;
      }

      toast.success('המוצר עודכן בהצלחה');
      navigate(`/admin?tab=products&edited=${id}`);
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('שגיאה בעדכון המוצר');
    } finally {
      setSaving(false);
    }
  };

  const validateSlug = async (slug: string, currentProductId: string) => {
    if (!slug) return false;

    const { data, error } = await supabase
      .from('products')
      .select('id')
      .eq('slug', slug)
      .neq('id', currentProductId)
      .maybeSingle();

    return !data && !error;
  };

  const updateProduct = (field: keyof Product, value: any) => {
    if (!product) return;

    console.log(`Updating product field: ${field}`, value);

    // When enabling group buy, clear conflicting sale fields
    // For group buy, inventory is not relevant - set to 0
    if (field === 'group_buy_enabled' && value === true) {
      setProduct({
        ...product,
        [field]: value,
        sale_type: 'immediate',
        sale_start: null,
        sale_end: null,
        total_inventory: 0 // Group buy products don't use inventory limits
      });
      return;
    }

    // When disabling group buy, reset inventory to 0
    if (field === 'group_buy_enabled' && value === false) {
      setProduct({
        ...product,
        [field]: value,
        total_inventory: 0
      });
      return;
    }

    const updatedProduct = { ...product, [field]: value };
    console.log('Updated product state:', updatedProduct);

    // Special logging for images updates
    if (field === 'images') {
      console.log('Images field updated:', {
        oldImages: product.images,
        newImages: value,
        currentProductState: product
      });
    }

    setProduct(updatedProduct);
  };

  const { stats, loading: statsLoading } = useAdminStats();

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">טוען...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">מוצר לא נמצא</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SecurityHeaders isAdminPage={true} preventIndexing={true} />
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AdminSidebar stats={stats} loading={statsLoading} />

          <SidebarInset className="flex-1 overflow-hidden">
            <div className="h-full px-4 py-8 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-6">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin?tab=products')}
                    className="flex items-center gap-2"
                  >
                    <ArrowRight className="h-4 w-4" />
                    חזרה לניהול
                  </Button>
                  <h1 className="text-xl font-bold flex-1">עריכת מוצר: {product.name}</h1>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/product/${product.slug}`, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    צפה במוצר
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>פרטי מוצר בסיסיים</CardTitle>
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <Checkbox
                            id="is_active"
                            checked={product.is_active}
                            onCheckedChange={(checked) => updateProduct('is_active', !!checked)}
                          />
                          <Label
                            htmlFor="is_active"
                            className={`font-medium ${product.is_active ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {product.is_active ? 'מוצר פעיל' : 'מוצר לא פעיל'}
                          </Label>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">שם המוצר *</Label>
                          <Input
                            id="name"
                            value={product.name}
                            onChange={(e) => updateProduct('name', e.target.value)}
                            placeholder="הזן שם המוצר"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="slug">URL המוצר *</Label>
                          <Input
                            id="slug"
                            value={product.slug || ''}
                            onChange={(e) => updateProduct('slug', e.target.value)}
                            placeholder="url-של-המוצר"
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            URL ייחודי למוצר (באנגלית בלבד, ללא רווחים)
                          </p>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="notes">הערות פנימיות (לא מוצג באתר)</Label>
                        <Textarea
                          id="notes"
                          value={product.notes || ''}
                          onChange={(e) => updateProduct('notes', e.target.value)}
                          placeholder="הערות לצוות (לשימוש פנימי בלבד)"
                          className="bg-muted/50"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="supplier">ספק</Label>
                          <div className="flex gap-2">
                            <Select
                              value={(product as any).supplier_id || ''}
                              onValueChange={(value) => updateProduct('supplier_id', value)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="בחר ספק" />
                              </SelectTrigger>
                              <SelectContent className="bg-background">
                                {suppliers.map((supplier) => (
                                  <SelectItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                    {supplier.city && ` (${supplier.city})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <SupplierQuickCreate onSupplierCreated={(id) => {
                              loadSuppliers();
                              updateProduct('supplier_id', id);
                            }} />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="supplier_sku">מק"ט ספק</Label>
                          <Input
                            id="supplier_sku"
                            value={product.supplier_sku || ''}
                            onChange={(e) => updateProduct('supplier_sku', e.target.value)}
                            placeholder="הזן מק״ט ספק"
                          />
                        </div>
                        <div>
                          <Label htmlFor="sku">מק"ט (לשימוש פנימי)</Label>
                          <Input
                            id="sku"
                            value={product.sku || ''}
                            onChange={(e) => updateProduct('sku', e.target.value)}
                            placeholder="הזן מק״ט"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description">תיאור המוצר</Label>
                        <Textarea
                          id="description"
                          value={product.description}
                          onChange={(e) => updateProduct('description', e.target.value)}
                          placeholder="הזן תיאור מפורט של המוצר (לא חובה)"
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>קטגוריות</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="category">קטגוריה *</Label>
                          <Select
                            value={product.category_id}
                            onValueChange={(value) => {
                              // Update both category_id and subcategory_id in a single state update
                              setProduct(prev => prev ? {
                                ...prev,
                                category_id: value,
                                subcategory_id: ''
                              } : prev);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="בחר קטגוריה" />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="subcategory">תת-קטגוריה *</Label>
                          <Select
                            value={product.subcategory_id}
                            onValueChange={(value) => updateProduct('subcategory_id', value)}
                            disabled={!product.category_id}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="בחר תת-קטגוריה" />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              {subcategories.map(subcat => (
                                <SelectItem key={subcat.id} value={subcat.id}>{subcat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="grade">דירוג מוצר *</Label>
                        <Select
                          value={product.grade || 'B'}
                          onValueChange={(value: 'A' | 'B' | 'C' | 'D') => updateProduct('grade', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="בחר דירוג" />
                          </SelectTrigger>
                          <SelectContent className="bg-background">
                            <SelectItem value="A">דירוג A - פרימיום (מצב מושלם, החזרה תוך 14 יום)</SelectItem>
                            <SelectItem value="B">דירוג B - סטנדרט (מצב טוב, החזרה תוך 7 ימים)</SelectItem>
                            <SelectItem value="C">דירוג C - כפי שהוא (ללא החזרה, אלא פגם מהותי)</SelectItem>
                            <SelectItem value="D">דירוג D - מכירה סופית (ללא החזרה בכל מקרה)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          דירוג המוצר קובע את מדיניות ההחזרות והאחריות
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Unified Sale Type Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>סוג מכירה</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Toggle between Regular, Group Buy, and Liquidation */}
                      <div>
                        <Label htmlFor="sale-mode">בחר סוג מכירה</Label>
                        <Select
                          value={
                            product.group_buy_enabled
                              ? "group_buy"
                              : product.liquidation_sale_enabled
                                ? "liquidation"
                                : "regular"
                          }
                          onValueChange={(value) => {
                            if (value === "group_buy") {
                              updateProduct('group_buy_enabled', true);
                              // updateProduct handles clearing other fields
                            } else if (value === "liquidation") {
                              setProduct(prev => prev ? {
                                ...prev,
                                group_buy_enabled: false,
                                liquidation_sale_enabled: true,
                                sale_type: 'liquidation',
                                total_inventory: prev.liquidation_total_inventory || 0
                              } : prev);
                            } else {
                              setProduct(prev => prev ? {
                                ...prev,
                                group_buy_enabled: false,
                                liquidation_sale_enabled: false,
                                sale_type: 'immediate'
                              } : prev);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background">
                            <SelectItem value="regular">מכירה רגילה</SelectItem>
                            <SelectItem value="group_buy">קבוצת רכישה</SelectItem>
                            <SelectItem value="liquidation">מכירת חיסול</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Group Buy Options */}
                      {product.group_buy_enabled && (
                        <SimpleGroupBuyFields
                          isGroupBuy={product.group_buy_enabled}
                          targetQuantity={product.group_buy_target_quantity}
                          endDate={product.group_buy_end_date}
                          lockedPrice={product.group_buy_locked_price}
                          basePrice={product.price_per_unit}
                          measurementUnit={product.measurement_unit}
                          limitToTarget={product.group_buy_limit_to_target || false}
                          onIsGroupBuyChange={(enabled) => updateProduct('group_buy_enabled', enabled)}
                          onTargetQuantityChange={(value) => updateProduct('group_buy_target_quantity', value)}
                          onEndDateChange={(value) => updateProduct('group_buy_end_date', value)}
                          onLockedPriceChange={(value) => updateProduct('group_buy_locked_price', value)}
                          onLimitToTargetChange={(value) => updateProduct('group_buy_limit_to_target', value)}
                        />
                      )}

                      {/* Liquidation Sale Options */}
                      {product.liquidation_sale_enabled && (
                        <LiquidationSaleFields
                          isLiquidation={product.liquidation_sale_enabled}
                          totalInventory={product.total_inventory} // Pass the main total_inventory
                          thresholdPercent={product.liquidation_threshold_percent}
                          deliveryDays={product.liquidation_delivery_days} // Now represents duration
                          measurementUnit={product.measurement_unit}
                          onIsLiquidationChange={(enabled) => updateProduct('liquidation_sale_enabled', enabled)}
                          onTotalInventoryChange={() => { }} // No-op, managed by main field
                          onThresholdPercentChange={(value) => updateProduct('liquidation_threshold_percent', value)}
                          onDeliveryDaysChange={(value) => updateProduct('liquidation_delivery_days', value)}
                          // Smart Suggestion Props
                          stepQuantity={product.step_quantity}
                          packageCoverage={product.package_coverage_per_unit}
                          minQuantity={product.min_quantity}
                          maxQuantity={product.max_quantity}
                        />
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>מחיר ומלאי</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Row 1: Measurement Unit & Inventory */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="measurement_unit">יחידת מדידה *</Label>
                          <Select
                            value={product.measurement_unit}
                            onValueChange={(value) => updateProduct('measurement_unit', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              <SelectItem value="unit">יח'</SelectItem>
                              <SelectItem value="square_meter">מ"ר</SelectItem>
                              <SelectItem value="liter">ליטר</SelectItem>
                              <SelectItem value="kilogram">ק"ג</SelectItem>
                              <SelectItem value="meter">מטר</SelectItem>
                              <SelectItem value="surface">משטח</SelectItem>
                              <SelectItem value="cubic_meter">מ"ק</SelectItem>
                              <SelectItem value="package_by_meter">חבילות (נמכר לפי מטר)</SelectItem>
                              <SelectItem value="package_by_square_meter">חבילות (נמכר לפי מ"ר)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {!product.group_buy_enabled && (
                          <div>
                            <Label htmlFor="total_inventory">מלאי כולל *</Label>
                            <Input
                              id="total_inventory"
                              type="number"
                              value={product.total_inventory}
                              onChange={(e) => {
                                const newInventory = parseInt(e.target.value) || 0;
                                updateProduct('total_inventory', newInventory);
                                // Reset max_quantity if it's higher than new inventory
                                if (product.max_quantity && product.max_quantity > newInventory) {
                                  updateProduct('max_quantity', newInventory);
                                }
                              }}
                              placeholder="0"
                              required
                              min={0}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              המלאי הכולל - לא רלוונטי לקבוצות רכישה
                            </p>
                          </div>
                        )}

                        {!product.group_buy_enabled && (
                          <div>
                            <Label htmlFor="original_inventory">מלאי מקורי (התחלתי)</Label>
                            <Input
                              id="original_inventory"
                              type="number"
                              value={product.original_inventory ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? null : parseInt(e.target.value);
                                updateProduct('original_inventory', val);
                              }}
                              placeholder="0"
                              min={0}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              למעקב: כמה מלאי היה בהתחלה (לפני מכירות)
                            </p>
                          </div>
                        )}
                        {product.group_buy_enabled && (
                          <div className="flex items-center p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              💡 בקבוצת רכישה אין הגבלת מלאי
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Row 2: Prices */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="price_per_unit">
                            {(product.measurement_unit === 'square_meter' || product.measurement_unit === 'package_by_square_meter')
                              ? 'מחיר נוכחי למ"ר *'
                              : (product.measurement_unit === 'meter' || product.measurement_unit === 'package_by_meter')
                                ? 'מחיר נוכחי למטר *'
                                : 'מחיר נוכחי ליח\' *'}
                          </Label>
                          <Input
                            id="price_per_unit"
                            type="number"
                            step="0.01"
                            value={product.price_per_unit}
                            onChange={(e) => updateProduct('price_per_unit', parseFloat(e.target.value))}
                            placeholder="0"
                            required
                          />
                          {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && product.package_coverage_per_unit && (
                            <div className="text-xs mt-1 p-2 bg-muted rounded">
                              <p className="font-semibold text-lime">מחיר חבילה: ₪{(product.price_per_unit * product.package_coverage_per_unit).toFixed(2)}</p>
                              <p className="text-muted-foreground">
                                {product.price_per_unit.toFixed(2)} ₪ × {product.package_coverage_per_unit} {product.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} = {(product.price_per_unit * product.package_coverage_per_unit).toFixed(2)} ₪ לחבילה
                              </p>
                            </div>
                          )}
                          {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && !product.package_coverage_per_unit && (
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠️ הגדר כיסוי חבילה בסעיף "הגדרות חבילות" למטה
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="cost_price">מחיר עלות (פנימי)</Label>
                          <Input
                            id="cost_price"
                            type="number"
                            step="0.01"
                            value={product.cost_price || ''}
                            onChange={(e) => updateProduct('cost_price', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="0"
                            className="bg-muted/30"
                          />
                          {product.price_per_unit > 0 && product.cost_price && (
                            <div className={`text-xs mt-1 p-2 rounded ${((product.price_per_unit - product.cost_price) / product.price_per_unit) * 100 < 20 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>
                              <div className="flex justify-between font-medium">
                                <span>רווח: ₪{(product.price_per_unit - product.cost_price).toFixed(2)}</span>
                                <span>{(((product.price_per_unit - product.cost_price) / product.price_per_unit) * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="original_price">מחיר מקורי (לפני הנחה)</Label>
                          <Input
                            id="original_price"
                            type="number"
                            step="0.01"
                            value={product.original_price || ''}
                            onChange={(e) => updateProduct('original_price', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="אופציונלי - להצגת הנחה"
                          />
                          {product.original_price && product.original_price > product.price_per_unit && (
                            <p className="text-xs text-lime font-semibold mt-1">
                              הנחה: {Math.round(((product.original_price - product.price_per_unit) / product.original_price) * 100)}%
                              {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && product.package_coverage_per_unit &&
                                ` (מחיר חבילה מקורי: ₪${(product.original_price * product.package_coverage_per_unit).toFixed(2)})`
                              }
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter')
                              ? 'המחיר המקורי ליחידת בסיס (מטר/מ"ר). יוצג כמחוק בדף המוצר'
                              : 'השאר ריק אם אין הנחה. יוצג כמחוק על המחיר הנוכחי'
                            }
                          </p>
                        </div>
                      </div>

                      {/* Quantity Settings */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="min_quantity">
                            כמות מינימלית
                            {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && (
                              <span className="text-muted-foreground font-normal">
                                {' '}(חבילות)
                              </span>
                            )}
                          </Label>
                          <Input
                            id="min_quantity"
                            type="number"
                            value={product.min_quantity}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              updateProduct('min_quantity', value);
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              if (product.max_quantity && value > product.max_quantity) {
                                toast.error(`הכמות המינימלית (${value}) לא יכולה להיות גבוהה מהכמות המקסימלית (${product.max_quantity})`);
                              }
                            }}
                            placeholder="1"
                          />
                          {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && (
                            <p className="text-xs text-muted-foreground mt-1">
                              מספר חבילות מינימלי להזמנה
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="max_quantity">
                            כמות מקסימלית להזמנה
                            {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && (
                              <span className="text-muted-foreground font-normal">
                                {' '}(חבילות)
                              </span>
                            )}
                          </Label>
                          <Input
                            id="max_quantity"
                            type="number"
                            value={product.max_quantity || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : null;
                              updateProduct('max_quantity', value);
                            }}
                            onBlur={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : null;
                              // Only check against inventory for non-group-buy products
                              if (!product.group_buy_enabled) {
                                const maxAllowed = product.total_inventory || 999999;
                                if (value && value > maxAllowed) {
                                  toast.error(`הכמות המקסימלית להזמנה לא יכולה לעלות על המלאי הכולל (${maxAllowed})`);
                                }
                              }
                              if (value && value < product.min_quantity) {
                                toast.error(`הכמות המקסימלית (${value}) לא יכולה להיות נמוכה מהכמות המינימלית (${product.min_quantity})`);
                              }
                            }}
                            placeholder="ללא הגבלה"
                            max={product.group_buy_enabled ? undefined : (product.total_inventory || undefined)}
                          />
                          {!product.group_buy_enabled && product.total_inventory && product.max_quantity && product.max_quantity > product.total_inventory && (
                            <div className="text-sm text-destructive mt-1">
                              הכמות המקסימלית לא יכולה לעלות על המלאי הכולל ({product.total_inventory})
                            </div>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="step_quantity">צעד כמות</Label>
                          <Input
                            id="step_quantity"
                            type="number"
                            value={product.step_quantity}
                            onChange={(e) => updateProduct('step_quantity', parseInt(e.target.value) || 1)}
                            placeholder="1"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter')
                              ? `קפיצות בכפולות של חבילות (מומלץ: 1)`
                              : 'בכמה יחידות הלקוח יכול לשנות את הכמות'}
                          </p>
                        </div>
                      </div>

                      {/* Package-specific settings - shown inline when package type is selected */}
                      {(product.measurement_unit === 'package_by_square_meter' || product.measurement_unit === 'package_by_meter') && (
                        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-4">
                          <h4 className="font-semibold text-amber-800 dark:text-amber-200">⚙️ הגדרות חבילה (חובה)</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="package_coverage">
                                כמה {product.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} בכל חבילה *
                              </Label>
                              <Input
                                id="package_coverage"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={product.package_coverage_per_unit || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updateProduct('package_coverage_per_unit', value ? parseFloat(value) : null);
                                }}
                                placeholder="לדוגמה: 2.7"
                                className={!product.package_coverage_per_unit ? 'border-amber-500' : ''}
                              />
                              {product.package_coverage_per_unit && product.price_per_unit && (
                                <p className="text-xs text-lime font-semibold mt-1">
                                  מחיר חבילה: ₪{(product.price_per_unit * product.package_coverage_per_unit).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            💡 שים לב: הכמות המינימלית, המקסימלית וצעד הכמות מוגדרים במספר חבילות.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>


                  <Card>
                    <CardHeader>
                      <CardTitle>מידות ומאפיינים</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_width">רוחב</Label>
                              <Input
                                id="dimension_width"
                                type="number"
                                step="0.01"
                                value={product.dimension_width || ''}
                                onChange={(e) => updateProduct('dimension_width', e.target.value ? parseFloat(e.target.value) : null)}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={product.dimension_width_unit || 'cm'}
                                onValueChange={(value) => updateProduct('dimension_width_unit', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_height">אורך</Label>
                              <Input
                                id="dimension_height"
                                type="number"
                                step="0.01"
                                value={product.dimension_height || ''}
                                onChange={(e) => updateProduct('dimension_height', e.target.value ? parseFloat(e.target.value) : null)}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={product.dimension_height_unit || 'cm'}
                                onValueChange={(value) => updateProduct('dimension_height_unit', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_depth">עובי</Label>
                              <Input
                                id="dimension_depth"
                                type="number"
                                step="0.01"
                                value={product.dimension_depth || ''}
                                onChange={(e) => updateProduct('dimension_depth', e.target.value ? parseFloat(e.target.value) : null)}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={product.dimension_depth_unit || 'mm'}
                                onValueChange={(value) => updateProduct('dimension_depth_unit', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                        <div>
                          <Label htmlFor="country_of_origin">ארץ ייצור</Label>
                          <Input
                            id="country_of_origin"
                            value={product.country_of_origin || ''}
                            onChange={(e) => updateProduct('country_of_origin', e.target.value)}
                            placeholder="לדוגמה: ספרד, איטליה, סין"
                          />
                        </div>
                        <div>
                          <Label htmlFor="material">חומר</Label>
                          <Input
                            id="material"
                            value={product.material || ''}
                            onChange={(e) => updateProduct('material', e.target.value)}
                            placeholder="לדוגמה: פורצלן, קרמיקה, עץ"
                          />
                        </div>
                        <div>
                          <Label htmlFor="finish">גימור</Label>
                          <Input
                            id="finish"
                            value={product.finish || ''}
                            onChange={(e) => updateProduct('finish', e.target.value)}
                            placeholder="לדוגמה: מט, מבריק, לפאטו"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>תמונות המוצר</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ProductImageGallery
                        images={product.images || []}
                        onImagesChange={(images) => updateProduct('images', images)}
                        productTitle={product.name}
                      />
                    </CardContent>
                  </Card>

                  <ProductAI
                    productId={product.id}
                    productName={product.name}
                    simulations={product.simulation_images || []}
                    onUpdate={(newSimulations) => updateProduct('simulation_images', newSimulations)}
                  />

                  {/* Connected Orders Section */}
                  <div className="mt-8">
                    <ProductOrders productId={product.id} />
                  </div>

                  <div className="flex justify-end gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/admin?tab=products')}
                    >
                      ביטול
                    </Button>
                    <Button type="submit" disabled={saving}>
                      <Save className="h-4 w-4 ml-2" />
                      {saving ? 'שומר...' : 'שמור שינויים'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
}